<template>
  <div>
    <navbar :links="links" />
    <div
      :class="{
        'desktop-mode': $store.state.common.desktopMode,
        'elevation-1': scrollTop > fixedSearchThreshold,
        firefox: $store.state.common.runtimeEnv.firefox,
        safari: $store.state.common.runtimeEnv.safari,
        tools: scrollTop <= fixedSearchThreshold,
        'tools-fixed': scrollTop > fixedSearchThreshold,
        'visible-drawer': $store.state.common.showDrawer,
      }"
    >
      <v-layout
        align-center
        wrap
        class="mt-2"
      >
        <!-- Select Players by Store Association Membernumber -->
        <v-btn
          v-if="showSelectByAssociationMemberNumberButton"
          color="grey darken-1"
          class="ml-auto mb-0 hoverable-icon"
          flat
          icon
          :title="$t('storeAssociationMemberNumberImport')"
          @click="showSelectByAssociationMemberNumberDialog = true"
        >
          <v-icon>playlist_add_check</v-icon>
        </v-btn>
        <!-- Search -->
        <v-text-field
          ref="searchbar"
          v-model="searchText"
          class="searchbar-input pt-0 mt-0"
          :class="{'ml-auto': !showSelectByAssociationMemberNumberButton}"
          single-line
          hide-details
          :label="searchLabel"
          clearable
          append-icon="search"
          @click:append="focusSearch()"
          @click:clear="clearSearch()"
          @keyup.native.esc="clearSearch()"
        />
        <!-- Country filter -->
        <v-slide-x-transition>
          <country-filter-menu
            v-if="Object.keys(countriesFromUserDecoders).length > 1"
            v-model="selectedCountryCodes"
            :countries="countriesFromUserDecoders"
            :active="
              selectedCountryCodes.length > 0 &&
                selectedCountryCodes.length < numCountriesFromUserDecoders
            "
          />
        </v-slide-x-transition>
        <!-- Include menu players checkbox -->
        <v-slide-x-transition>
          <v-checkbox
            v-if="
              !!$userprofile &&
                !!$userprofile.workspace &&
                $userprofile.workspace.has_menus
            "
            v-model="includeMenuPlayers"
            color="msr"
            class="ml-4"
            style="flex-grow: 0"
            :label="$t('addDecoderToSpotIncludeMenuPlayers')"
            prepend-icon="restaurant"
          />
        </v-slide-x-transition>
        <!-- Select/deselect all Decoders -->
        <div
          class="ml-auto"
          :class="{
            'mr-5':
              $vuetify.breakpoint.smAndUp && scrollTop > fixedSearchThreshold,
          }"
        >
          <v-checkbox
            :input-value="allSelected"
            class="msr--text ml-3"
            color="msr"
            hide-details
            :title="
              allSelected ? $t('deselectAllDecoders') : $t('selectAllDecoders')
            "
            :indeterminate="someSelected"
            @change="(selected) => (allSelected = selected)"
          />
        </div>
      </v-layout>
    </div>
    <!-- Fixed spacer for tools widget -->
    <div
      v-if="scrollTop > fixedSearchThreshold"
      class="tools-fixed-spacer"
    />
    <!-- Closingtime RadioSpot needs Store opening hours warning -->
    <div
      v-if="!loading && spot && decoders && isClosingTimeRadioSpot(spot)"
      class="pa-2"
    >
      {{ $t('closingtimeAddDecodersOpeningHoursWarning') }}<br><router-link
        to="/openinghours"
      >
        {{ $t('closingtimeManageOpeningHoursNow') }}
      </router-link>
    </div>
    <!-- No available Decoders warning -->
    <v-list v-if="!loading && decoders && !filteredStoreDecoderWrappers.length">
      <v-subheader>{{ $t('noAvailableDecoders') }}</v-subheader>
    </v-list>
    <!-- Decoder list -->
    <selectable-store-decoders
      v-for="(wrapper, wrapperIndex) in filteredStoreDecoderWrappers"
      :key="wrapper.store.id"
      ref="selectableStoreDecoders"
      :store="wrapper.store"
      :decoders="wrapper.decoders"
      :preselect="decoders.length === 1"
      :show-divider="wrapperIndex !== filteredStoreDecoderWrappers.length - 1"
      show-select-all-option
      @preselected="
        allSelected =
          decoders.length > 0 &&
          getSelectedDecoders().length === decoders.length
      "
    />
    <!-- Empty placeholder to allow a bit more scrolling -->
    <div style="height: 100px" />
    <!-- Add selected Decoders button -->
    <v-slide-y-reverse-transition>
      <v-btn
        v-if="atLeastOneDecoderSelected && !showConfirmationDialog"
        dark
        round
        fixed
        bottom
        right
        data-cy="continue-btn"
        :class="$desktopModeStyles"
        class="msr z-index-1"
        @click.stop="onContinueButtonClicked"
      >
        <v-icon left>
          arrow_forward
        </v-icon>
        {{ $t('goForward') }}
      </v-btn>
    </v-slide-y-reverse-transition>
    <!-- Loading Indicator -->
    <div class="loading-indicator">
      <v-progress-circular
        v-if="loading"
        :size="50"
        indeterminate
        class="mt-5 msr--text"
      />
    </div>
    <!-- Confirmation Dialog -->
    <entry-creation-dialog
      v-if="spot"
      ref="confirmationDialog"
      :show.sync="showConfirmationDialog"
      :contents="contentsForCreationDialog"
      :title="dialogTitle"
      :busy="adding"
      :show-hotrotation="!isClosingTimeRadioSpot(spot)"
      :show-date-controls="!isClosingTimeRadioSpot(spot)"
      :show-timeframe-controls="!isClosingTimeRadioSpot(spot)"
      @aborted="handleDialogAbortion()"
      @confirmed="handleDialogConfirmation"
    />
    <offline-decoder-selected-dialog
      :show.sync="showOfflineDecoderSelectedDialog"
      :offline-decoders="getSelectedOfflineDecoders()"
      @confirmed="showConfirmationDialog = true"
    />
    <!-- Select by Store Association Membernumber Dialog -->
    <decoder-selection-import-dialog
      v-if="showSelectByAssociationMemberNumberButton"
      :show.sync="showSelectByAssociationMemberNumberDialog"
      :decoders="decoders"
      @matched="selectDecodersFromAssociatedMemberNumbers"
    />
  </div>
</template>

<script>
import {getMainTVSpotFromBundle} from '@/utils';

import addDecoderTo from '@/mixins/addDecoderTo.js';
import identifiers from '@/mixins/identifiers.js';
import suppressWarnings from '@/mixins/suppressWarnings.js';
import trackScrollTop from '@/mixins/trackScrollTop.js';

import CountryFilterMenu from '@/components/CountryFilterMenu';
import DecoderSelectionImportDialog from '@/components/DecoderSelectionImportDialog';
import EntryCreationDialog from '@/components/EntryCreationDialog';
import Navbar from '@/components/Navbar';
import OfflineDecoderSelectedDialog from '@/components/OfflineDecoderSelectedDialog';
import SelectableStoreDecoders from '@/components/SelectableStoreDecoders';

export default {
  components: {
    CountryFilterMenu,
    DecoderSelectionImportDialog,
    EntryCreationDialog,
    Navbar,
    OfflineDecoderSelectedDialog,
    SelectableStoreDecoders,
  },
  mixins: [addDecoderTo, identifiers, suppressWarnings, trackScrollTop],
  data() {
    return {
      links: ['addDecoderToSpot'],
      spot: null,

      selectedCountryCodes: [],
      userDecoders: [],

      showSelectByAssociationMemberNumberDialog: false,
      associationMemberNumbersText: '',
      expandUnknownMemberNumbers: false,
    };
  },
  computed: {
    includeMenuPlayers: {
      get() {
        return this.$store.state.common.includeMenuPlayers;
      },
      set(includeMenuPlayers) {
        this.$store.commit('SET_INCLUDE_MENU_PLAYERS', includeMenuPlayers);
      },
    },
    countriesFromUserDecoders() {
      const stores = this.userDecoders.map((decoder) => decoder.store);
      const codeToName = {};
      stores.forEach((store) => {
        codeToName[store.countrycode] = store.countryname;
      });
      return codeToName;
    },
    numCountriesFromUserDecoders() {
      return Object.keys(this.countriesFromUserDecoders).length;
    },
    /**
     * Filter Decoders by optional Country codes.
     *
     * The name here is generic as the addDecoderTo mixin depends on it.
     */
    decoders() {
      return this.userDecoders.filter((decoder) => {
        const countryFilterNotUsed = this.selectedCountryCodes.length === 0;
        if (countryFilterNotUsed) {
          return true;
        }
        const countryCodeMatchesFilter =
          this.selectedCountryCodes.indexOf(decoder.store.countrycode) !== -1;
        return countryCodeMatchesFilter;
      });
    },
    contentsForCreationDialog() {
      return this.spot ? [this.spot] : [];
    },
    showSelectByAssociationMemberNumberButton() {
      return (
        this.$userprofile &&
        this.$userprofile.can_set_store_association_membernumber
      );
    },
  },
  watch: {
    includeMenuPlayers: function () {
      this.resetDecoderSelection();
      this.fetchUserDecoders();
    },
  },
  mounted() {
    let vm = this;

    // Handle optional query parameters.
    const query = this.$route.query;
    if (query.search) {
      this.searchText = query.search;
    }

    this.fetchContent().then(() => {
      this.updateNavbarLinks(vm.spot);
    });

    this.fetchUserDecoders();
  },
  methods: {
    onContinueButtonClicked() {
      const offlineDecoders = this.getSelectedOfflineDecoders();
      if (
        offlineDecoders.length &&
        !this.isSuppressedOfflineDecoderSelectedWarning()
      ) {
        this.showOfflineDecoderSelectedDialog = true;
        return;
      }
      this.showConfirmationDialog = true;
    },
    selectDecodersFromAssociatedMemberNumbers(matchedDecoders) {
      // // Note: This relies on mixins/addDecoderTo.js
      this.resetDecoderSelection();

      const opts = {
        selected: true,
        decoderIdents: matchedDecoders.map((decoder) => decoder.ident),
      };
      this.$store.commit('SET_DECODERS_SELECTED_BY_IDENT', opts);
    },
    /**
     * Get Spot description and insert a link to it.
     */
    updateNavbarLinks(spot) {
      let title = spot.title;
      if (this.isTVSpotBundle(spot)) {
        const bundle = spot;
        title = getMainTVSpotFromBundle(bundle).title;
      }
      const link = {
        label: title,
        path: '/spot/' + spot.ctype + '/' + spot.id,
      };
      const lastLink = {
        label: 'addDecoderToSpot',
        active: false,
      };
      this.links = [link, lastLink];
    },
    /**
     * Args:
     *
     *   additionalData (Object):
     *
     *     overlayData (object): Optional, is passed when a TVSpot with
     *       overlay_options has been activated and must then be handed
     *       to the API call as well.
     *
     *     timeframeIds (Array[Number]): Optional timeframe IDs to associate with.
     */
    handleDialogConfirmation(additionalData) {
      const decoders = this.getSelectedDecoders();
      this.makeDecodersUseSpot(decoders, additionalData || {});
    },
    makeDecodersUseSpot(decoders, additionalData) {
      const vm = this;

      const dialogOptions = this.$refs.confirmationDialog.options;
      const startDate = dialogOptions.expandDateControls
        ? this.$refs.confirmationDialog.startDate
        : '';
      const stopDate = dialogOptions.expandDateControls
        ? this.$refs.confirmationDialog.stopDate
        : '';
      const minutesBeforeClosing =
        this.$refs.confirmationDialog.minutesBeforeClosing;
      const weekDays = this.$refs.confirmationDialog.weekDays;

      const spotIds = [this.$route.params.spotId];
      const decoderIds = decoders.map((decoder) => decoder.id);
      const opts = {
        closingtime_minutes_before_closing: minutesBeforeClosing,
        hotrotation: dialogOptions.hotrotation,
        startdate: startDate,
        stopdate: stopDate,
      };
      if (weekDays) {
        opts.weekdays = weekDays;
      }
      if (additionalData.spotIdToOverlayData) {
        opts.spotIdToOverlayData = additionalData.spotIdToOverlayData;
      }
      if (
        additionalData.timeframeIds &&
        additionalData.timeframeIds.length > 0
      ) {
        opts.timeframeIds = additionalData.timeframeIds;
      }

      vm.adding = true;
      const promise = this.$api.createSpotEntries(
        this.$route.params.ctype,
        decoderIds,
        spotIds,
        opts
      );
      promise
        .then((response) => {
          vm.reset();
          vm.adding = false;
          vm.handleDialogAbortion();

          vm.$bus.$emit('alert-success', {
            message: 'spotsAddedSuccess',
            translate: true,
          });
          // Go back to Spot which we added this to.
          vm.$store.commit('UNSET_PRE_ROUTE_QUESTION');
          vm.goToSpot(vm.$route.params.ctype, vm.$route.params.spotId);
        })
        .catch((error) => {
          let message = 'genericFailFeedback';
          let timeout = 1500;

          if (
            error &&
            error.response &&
            error.response.data &&
            error.response.data.detail
          ) {
            message = error.response.data.detail;
            timeout = 15000;
          }

          vm.$bus.$emit('alert-error', {
            message: message,
            translate: true,
            timeout: timeout,
          });

          // Reset.
          vm.adding = false;
        });
    },
    goToSpot(ctype, spotId) {
      let path = '/spot/' + ctype + '/' + spotId;
      this.$router.push(path);
    },
    fetchContent() {
      const promise = this.$api.retrieveContent(
        this.$route.params.ctype,
        this.$route.params.spotId
      );
      promise.then((response) => {
        this.spot = response.data;
      });
      return promise;
    },
    fetchUserDecoders() {
      this.loading = true;
      this.$api
        .listUserDecoders(
          this.$route.params.ctype,
          this.$route.params.spotId,
          this.includeMenuPlayers
        )
        .then((response) => {
          this.userDecoders = response.data;
          this.loading = false;
        });
    },
  },
};
</script>

<style src="@/mixins/addDecoderTo.global.css"></style>

<style scoped src="@/mixins/addDecoderTo.scoped.css"></style>



// WEBPACK FOOTER //
// src/pages/AddDecoderToSpot.vue